import React, { useCallback, useMemo, useRef, useState } from 'react';
import { zip } from 'fflate';
import {
  Upload, Download, X, Image as ImageIcon, Loader2, CheckCircle2,
  AlertCircle, Trash2, Sparkles, Wand2,
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

// ── Çıktı formatları (Gemini imageConfig yalnızca bu oranları destekler) ──────
const FORMATS = [
  { id: 'square', label: 'Kare 1:1', aspect: '1:1', hint: 'Center the product with a balanced, symmetrical 1:1 composition.' },
  { id: 'story', label: 'Story 9:16', aspect: '9:16', hint: 'Vertical 9:16 composition; place the product in the lower-center third and leave airy negative space in the upper part for a headline.' },
  { id: 'banner', label: 'Banner 16:9', aspect: '16:9', hint: 'Wide 16:9 banner; place the product off to one side following the rule of thirds and keep generous clean background on the opposite side for marketing text.' },
];

// ── Hazır konsept/tema preset'leri ────────────────────────────────────────────
const THEMES = [
  { id: 'summer', emoji: '☀️', label: 'Yaz / Sahil', prompt: 'A bright, cheerful summer beach scene in vivid MIDDAY sunshine (NOT golden hour, NOT a dark or moody sunset): the product sits naturally on clean light sand near the shoreline, sparkling turquoise sea and a clear bright blue sky behind, crisp lively sunlight that makes the gold and the water sparkle, fresh white wave foam, a few seashells. High-key, airy, vibrant and uplifting holiday-fun vibe with punchy summer colors. Achieve separation for the product with crisp light, sparkle and shallow depth of field — keep the whole image bright and sunny, never dim.' },
  { id: 'beachclub', emoji: '🏖️', label: 'Beach Club (Bodrum/Çeşme)', prompt: 'An elegant Aegean beach-club still life with a Bodrum / Cesme vibe, kept deliberately CLEAN and MINIMAL so nothing competes with the jewellery\'s true small size. The bracelet rests naturally on a close-up surface — sun-bleached teak, woven rattan or a pale linen napkin on a bar counter. STRICT SCALE RULE: do NOT place ANY large human-scale props in the frame — absolutely no handbag, no straw bag, no sunglasses, no glasses, no bottles, no cocktails, no plates; these objects destroy the sense of scale and make the bracelet look oversized. The ONLY decorative elements allowed are things that are SMALLER than or about the same size as the bracelet: at most a single small seashell, a few grains of fine sand, a tiny sprig of greenery, or scattered water droplets. The whole beach-club atmosphere must come ONLY from a softly and heavily blurred background bokeh — white parasols, flowing linen cabana curtains, sun loungers, out-of-focus glassware shelves and the turquoise sea far behind, all well out of focus. Warm golden afternoon light, gentle reflections, relaxed bohemian-luxe holiday mood. The bracelet stays small, true-to-life and occupies only a modest part of a tasteful composition.' },
  { id: 'halhal', emoji: '🩴', label: 'Halhal (Ayak Bilekliği)', prompt: 'IMPORTANT: this product is an ANKLET (a halhal), meant to be worn around a woman\'s ANKLE — not a wrist bracelet. Show it worn naturally and elegantly around a slim, well-groomed bare ankle of a model with smooth sun-kissed skin, the foot relaxed and barefoot. The anklet sits true-to-life snug around the ankle bone, draping with realistic gravity, gold and gemstones catching soft directional sunlight. Set it in a fresh summery lifestyle scene: the model reclining on warm light sand at the shoreline, or dipping a foot at the edge of a turquoise pool, or stepping across smooth beach pebbles — clean, bright, holiday vibe with airy negative space. Keep the framing close on the lower leg and ankle so the anklet is clearly the hero, in correct human-scale proportion. Tasteful editorial barefoot-summer mood, soft skin tones, shallow depth of field. Do not add wrist jewellery or place the product on a hand.' },
  { id: 'tropical', emoji: '🌴', label: 'Tropik / Havuz', prompt: 'A luxurious tropical poolside scene: turquoise water, palm and monstera leaves, bright sunshine, fresh water droplets, vibrant summer-vacation atmosphere.' },
  { id: 'autumn', emoji: '🍂', label: 'Sonbahar', prompt: 'A cozy autumn setting: warm fallen leaves, a rustic wooden surface, soft diffused daylight, amber and rust tones, calm seasonal mood.' },
  { id: 'winter', emoji: '❄️', label: 'Kış / Kar', prompt: 'A serene winter scene: soft fresh snow, frosted evergreen branches, cool blue-white ambient light with a warm highlight on the product, calm cozy mood.' },
  { id: 'newyear', emoji: '🎄', label: 'Yılbaşı', prompt: 'A festive new-year scene: warm golden bokeh string lights, pine branches, subtle satin gift ribbons, elegant celebratory atmosphere.' },
  { id: 'luxury', emoji: '💎', label: 'Lüks / Mermer', prompt: 'An elegant luxury still life: a polished marble surface, soft draped silk fabric, refined studio lighting, tasteful gold accents, premium high-end mood.' },
  { id: 'spring', emoji: '🌸', label: 'Bahar / Çiçek', prompt: 'A fresh spring scene: delicate blossoms and green leaves, soft natural daylight, pastel tones, light and romantic mood.' },
  { id: 'studio', emoji: '🎨', label: 'Stüdyo Gradyan', prompt: 'A clean studio backdrop with a smooth pastel color gradient, a soft contact shadow beneath the product, minimal modern e-commerce look.' },
];

// Ürünü AYNEN koruyup etrafına sahne kuran ana talimat.
const PRESERVE_BASE =
  'You are a high-end commercial product photographer. The attached image is a REAL product. ' +
  'Recreate this EXACT product — identical shape, proportions, materials, colors, gemstones, ' +
  'engravings, logos, text and every fine detail — and place it naturally into the scene described below. ' +
  'Do NOT redesign, restyle, recolor, deform or invent any part of the product; it must stay perfectly ' +
  'faithful and instantly recognizable. Build a photorealistic environment around it with natural, ' +
  'physically-correct lighting, soft realistic shadows and subtle reflections that match the scene. ' +
  'CRITICAL SCALE: this is a small piece of jewellery (a bracelet only about 6 cm / 2.5 inches across). ' +
  'Render it at a realistic, true-to-life size relative to every other object — it must sit naturally and ' +
  'must NOT be oversized, must NOT dominate or fill the frame. Any prop (a glass, sunglasses, a bag, plates) ' +
  'must be correctly larger than the bracelet, with believable real-world proportions and perspective. ' +
  'The bracelet should occupy only a modest part of the composition, like a real lifestyle product photo. ' +
  'NATURAL STYLING & SEPARATION: art-direct this like a tasteful editorial product photographer. Let the bracelet ' +
  'rest or drape NATURALLY and effortlessly on a believable surface — never balanced on an artificial pile or stack ' +
  'of stones, never a gimmicky pedestal. Make the product separate cleanly from the background through elegant craft, ' +
  'not props: choose a surface, angle and tone that gently contrast the product, lift it with shallow depth of field, ' +
  'and use soft directional light so the gold and gemstones quietly catch the light. If the natural surface is a ' +
  'similar tone to the product (e.g. pale sand), introduce subtle separation organically — a soft shadow, slightly ' +
  'darker or wet ground, dappled light or a complementary backdrop — keeping everything realistic and understated. ' +
  'Sharp focus on the product, tasteful depth of field, premium advertising look. ' +
  'Keep one side of the background calm and uncluttered (softly blurred scenery, not a blank white block) ' +
  'so marketing text can be added later. Output a single image.';

function buildPrompt(scenePrompt, formatHint) {
  return `${PRESERVE_BASE}\n\nSCENE: ${scenePrompt}\n\nCOMPOSITION: ${formatHint}`;
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Görsel yüklenemedi')); };
    img.src = url;
  });
}

// Dosyayı en fazla 1280px kenara indir + base64 payload üret (referans olarak yeterli, hafif).
async function fileToPayload(file) {
  const { img } = await loadImage(file);
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const maxEdge = 1280;
  const scale = Math.min(1, maxEdge / Math.max(iw, ih));
  const cw = Math.max(1, Math.round(iw * scale));
  const ch = Math.max(1, Math.round(ih * scale));
  const canvas = document.createElement('canvas');
  canvas.width = cw; canvas.height = ch;
  const ctx = canvas.getContext('2d');
  // beyaz zemin (webp/png şeffaflığı jpeg'de siyah olmasın)
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, cw, ch);
  ctx.drawImage(img, 0, 0, cw, ch);
  const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
  return {
    dataUrl,
    payload: { mimeType: 'image/jpeg', data: dataUrl.split(',')[1] || '' },
    w: iw, h: ih,
  };
}

async function callGeminiScene({ payload, aspect, prompt, model, brandId }) {
  const resp = await fetch(`${API_BASE}/api/ai/gemini/chat`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' },
    body: JSON.stringify({
      model,
      aspectRatio: aspect,
      temperature: 0.6,
      // image output models can exhaust the default 1024 token budget and return
      // NO image (finishReason MAX_TOKENS) — give them headroom.
      maxOutputTokens: 8192,
      messages: [{ role: 'user', content: prompt, images: [payload] }],
    }),
  });
  const txt = await resp.text();
  let json;
  try { json = JSON.parse(txt); } catch { json = { message: txt }; }
  if (!resp.ok) {
    const d = json?.details;
    const ds = !d ? '' : (typeof d === 'string' ? d.slice(0, 300) : (() => { try { return JSON.stringify(d).slice(0, 300); } catch { return ''; } })());
    throw new Error([json?.error, ds].filter(Boolean).join(' — ') || resp.statusText);
  }
  const images = Array.isArray(json?.images) ? json.images : [];
  if (!images.length) {
    const mt = (json?.message || '').trim();
    throw new Error(mt ? `Model görsel yerine yazı döndü: "${mt.slice(0, 160)}"` : 'Model görsel üretmedi.');
  }
  return images[0]; // { mimeType, data }
}

function b64ToBytes(b64) {
  const bin = atob(b64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function uid() {
  return Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-4);
}

function stamp() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}`;
}

export default function ProductConcept() {
  const [sources, setSources] = useState([]); // [{ id, name, dataUrl, payload, w, h }]
  const [selectedFormats, setSelectedFormats] = useState(() => FORMATS.map((f) => f.id));
  const [themeId, setThemeId] = useState('summer');
  const [freePrompt, setFreePrompt] = useState('');
  const [model, setModel] = useState('gemini-3-pro-image-preview');
  const [running, setRunning] = useState(false);
  const [zipBuilding, setZipBuilding] = useState(false);
  const [outputs, setOutputs] = useState([]); // [{ id, srcId, srcName, formatId, formatLabel, status, dataUrl, mimeType, data, error }]
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  const brandId = useMemo(() => {
    try {
      return (
        localStorage.getItem('currentBrandId') ||
        localStorage.getItem('selectedBrandId') ||
        sessionStorage.getItem('currentBrandId') ||
        sessionStorage.getItem('selectedBrandId') || ''
      );
    } catch (_) { return ''; }
  }, []);

  const activeFormats = useMemo(
    () => FORMATS.filter((f) => selectedFormats.includes(f.id)),
    [selectedFormats],
  );
  const theme = useMemo(() => THEMES.find((t) => t.id === themeId) || THEMES[0], [themeId]);
  const scenePrompt = useMemo(() => {
    const free = freePrompt.trim();
    return free ? `${theme.prompt} ${free}` : theme.prompt;
  }, [theme, freePrompt]);

  const addFiles = useCallback(async (fileList) => {
    setError('');
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (!files.length) return;
    const loaded = [];
    for (const f of files) {
      try {
        const r = await fileToPayload(f);
        loaded.push({ id: uid(), name: f.name.replace(/\.[^.]+$/, ''), ...r });
      } catch (e) {
        setError(`"${f.name}" yüklenemedi: ${e.message || e}`);
      }
    }
    if (loaded.length) setSources((prev) => [...prev, ...loaded]);
  }, []);

  const onFileInput = (e) => { addFiles(e.target.files); e.target.value = ''; };
  const onDrop = (e) => {
    e.preventDefault(); dragCounter.current = 0; setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };
  const onDragEnter = (e) => { e.preventDefault(); dragCounter.current += 1; setIsDragging(true); };
  const onDragLeave = (e) => { e.preventDefault(); dragCounter.current -= 1; if (dragCounter.current <= 0) setIsDragging(false); };

  const removeSource = (id) => setSources((p) => p.filter((s) => s.id !== id));
  const toggleFormat = (id) => setSelectedFormats((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const generate = useCallback(async () => {
    if (running) return;
    if (!sources.length) { setError('Önce en az bir ürün görseli yükle.'); return; }
    if (!activeFormats.length) { setError('En az bir format seç.'); return; }
    setError('');
    setRunning(true);

    // Tüm (görsel × format) işlerini pending olarak kur
    const jobs = [];
    for (const s of sources) {
      for (const f of activeFormats) {
        jobs.push({ id: uid(), srcId: s.id, srcName: s.name, src: s, fmt: f });
      }
    }
    setOutputs(jobs.map((j) => ({
      id: j.id, srcId: j.srcId, srcName: j.srcName, formatId: j.fmt.id,
      formatLabel: j.fmt.label, status: 'pending', dataUrl: null, error: null,
    })));

    // Sıralı çalıştır (dev-token rate limitine saygı)
    for (const j of jobs) {
      try {
        const prompt = buildPrompt(scenePrompt, j.fmt.hint);
        const img = await callGeminiScene({ payload: j.src.payload, aspect: j.fmt.aspect, prompt, model, brandId });
        const dataUrl = `data:${img.mimeType || 'image/png'};base64,${img.data}`;
        setOutputs((prev) => prev.map((o) => o.id === j.id
          ? { ...o, status: 'done', dataUrl, mimeType: img.mimeType || 'image/png', data: img.data } : o));
      } catch (e) {
        setOutputs((prev) => prev.map((o) => o.id === j.id
          ? { ...o, status: 'error', error: e.message || String(e) } : o));
      }
    }
    setRunning(false);
  }, [running, sources, activeFormats, scenePrompt, model, brandId]);

  const downloadOne = (o) => {
    if (!o?.dataUrl) return;
    const a = document.createElement('a');
    const ext = (o.mimeType || 'image/png').includes('jpeg') ? 'jpg' : 'png';
    a.href = o.dataUrl;
    a.download = `${o.srcName}_${theme.id}_${o.formatId}.${ext}`;
    a.click();
  };

  const downloadZip = useCallback(async () => {
    const done = outputs.filter((o) => o.status === 'done' && o.data);
    if (!done.length) return;
    setZipBuilding(true);
    try {
      const files = {};
      const counts = {};
      for (const o of done) {
        const ext = (o.mimeType || 'image/png').includes('jpeg') ? 'jpg' : 'png';
        let name = `${o.srcName}_${theme.id}_${o.formatId}.${ext}`;
        counts[name] = (counts[name] || 0) + 1;
        if (counts[name] > 1) name = name.replace(`.${ext}`, `_${counts[name]}.${ext}`);
        files[name] = b64ToBytes(o.data);
      }
      const zipped = await new Promise((resolve, reject) =>
        zip(files, { level: 6 }, (err, data) => (err ? reject(err) : resolve(data))));
      const blob = new Blob([zipped], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `product-concept_${theme.id}_${stamp()}.zip`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(`ZIP oluşturulamadı: ${e.message || e}`);
    } finally {
      setZipBuilding(false);
    }
  }, [outputs, theme]);

  const doneCount = outputs.filter((o) => o.status === 'done').length;
  const totalCount = outputs.length;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white">
          <Wand2 className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ürün Konsept Stüdyosu <span className="text-sm font-normal text-violet-500">· Nano Banana</span></h1>
          <p className="text-sm text-gray-500">Ürün görselini yükle → bir konsept seç → ürünü bozmadan yazlık/temalı yaşam tarzı sahnelerine oturt. Kare, story ve banner formatında çıktı.</p>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> <span>{error}</span>
        </div>
      )}

      {/* 1) Ürün görselleri */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="mb-3 text-sm font-semibold text-gray-700">1 · Ürün görselleri</div>
        <div
          onClick={() => fileInputRef.current?.click()}
          onDrop={onDrop} onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDragOver={(e) => e.preventDefault()}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-8 text-center transition ${isDragging ? 'border-violet-400 bg-violet-50' : 'border-gray-300 hover:border-violet-300 hover:bg-gray-50'}`}
        >
          <Upload className="mb-2 h-7 w-7 text-gray-400" />
          <div className="text-sm font-medium text-gray-600">Görselleri sürükle veya tıkla</div>
          <div className="text-xs text-gray-400">PNG / JPG / WEBP · birden fazla olabilir</div>
          <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={onFileInput} />
        </div>

        {sources.length > 0 && (
          <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
            {sources.map((s) => (
              <div key={s.id} className="group relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                <img src={s.dataUrl} alt={s.name} className="h-24 w-full object-contain" />
                <button onClick={() => removeSource(s.id)}
                  className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-gray-500 opacity-0 shadow group-hover:opacity-100 hover:text-red-600">
                  <X className="h-3.5 w-3.5" />
                </button>
                <div className="truncate px-1.5 py-1 text-[10px] text-gray-500">{s.name}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 2) Konsept */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="mb-3 text-sm font-semibold text-gray-700">2 · Konsept</div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {THEMES.map((t) => (
            <button key={t.id} onClick={() => setThemeId(t.id)}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${themeId === t.id ? 'border-violet-400 bg-violet-50 text-violet-700 ring-1 ring-violet-300' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              <span className="text-base">{t.emoji}</span> {t.label}
            </button>
          ))}
        </div>
        <div className="mt-3">
          <label className="text-xs font-medium text-gray-500">Ekstra yönlendirme (opsiyonel)</label>
          <textarea
            value={freePrompt}
            onChange={(e) => setFreePrompt(e.target.value)}
            rows={2}
            placeholder="örn. yanında deniz kabukları olsun, akşam üstü pembe gökyüzü, üstte 'YENİ SEZON' için boşluk bırak…"
            className="mt-1 w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
          />
          <p className="mt-1 text-[11px] text-gray-400">Seçilen konseptin üstüne eklenir. Ürünün kendisi her durumda korunur.</p>
        </div>
      </div>

      {/* 3) Format + model + üret */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="mb-3 text-sm font-semibold text-gray-700">3 · Format & üretim</div>
        <div className="flex flex-wrap items-center gap-2">
          {FORMATS.map((f) => (
            <button key={f.id} onClick={() => toggleFormat(f.id)}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition ${selectedFormats.includes(f.id) ? 'border-violet-400 bg-violet-50 text-violet-700 ring-1 ring-violet-300' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
              {f.label}
            </button>
          ))}
          <div className="mx-2 h-6 w-px bg-gray-200" />
          <div className="inline-flex overflow-hidden rounded-lg border border-gray-200 text-xs">
            <button onClick={() => setModel('gemini-3-pro-image-preview')}
              className={`px-2.5 py-1.5 font-medium ${model === 'gemini-3-pro-image-preview' ? 'bg-violet-100 text-violet-700' : 'bg-gray-50 text-gray-500'}`}>
              Nano Banana Pro
            </button>
            <button onClick={() => setModel('gemini-2.5-flash-image')}
              className={`px-2.5 py-1.5 font-medium ${model === 'gemini-2.5-flash-image' ? 'bg-violet-100 text-violet-700' : 'bg-gray-50 text-gray-500'}`}>
              Flash (hızlı)
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button onClick={generate} disabled={running || !sources.length || !activeFormats.length}
            className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {running ? `Üretiliyor… (${doneCount}/${totalCount})` : `Üret (${sources.length} görsel × ${activeFormats.length} format = ${sources.length * activeFormats.length})`}
          </button>
          {doneCount > 0 && (
            <button onClick={downloadZip} disabled={zipBuilding}
              className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
              {zipBuilding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              ZIP indir ({doneCount})
            </button>
          )}
        </div>
        <p className="mt-2 text-[11px] text-gray-400">
          Not: Üretim sıralı çalışır (her görsel-format ayrı API çağrısı). Çok ince logo/yazı detayları nadiren kayabilir — en sadık sonuç için <b>Nano Banana Pro</b> önerilir.
        </p>
      </div>

      {/* Sonuçlar */}
      {outputs.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {outputs.map((o) => (
            <div key={o.id} className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
              <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
                <div className="truncate text-xs font-medium text-gray-600">{o.srcName} · {o.formatLabel}</div>
                {o.status === 'done' && (
                  <button onClick={() => downloadOne(o)} className="text-gray-400 hover:text-violet-600"><Download className="h-4 w-4" /></button>
                )}
              </div>
              <div className="flex aspect-square items-center justify-center bg-gray-50">
                {o.status === 'pending' && <Loader2 className="h-6 w-6 animate-spin text-gray-300" />}
                {o.status === 'error' && (
                  <div className="flex flex-col items-center gap-1 px-4 text-center">
                    <AlertCircle className="h-6 w-6 text-red-400" />
                    <div className="text-[11px] text-red-500">{o.error}</div>
                  </div>
                )}
                {o.status === 'done' && <img src={o.dataUrl} alt={o.formatLabel} className="h-full w-full object-contain" />}
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 text-[11px]">
                {o.status === 'done' && <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> <span className="text-gray-400">Hazır</span></>}
                {o.status === 'pending' && <span className="text-gray-400">Sırada…</span>}
                {o.status === 'error' && <span className="text-red-400">Hata</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {outputs.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-gray-200 py-12 text-center text-gray-400">
          <ImageIcon className="h-8 w-8" />
          <div className="text-sm">Henüz çıktı yok — görsel yükle, konsept seç ve <b>Üret</b>'e bas.</div>
        </div>
      )}
    </div>
  );
}
